import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }
}

// SETUP COLORS

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
};

const PRIMARY = {
  lighter: '#CCFFE8',
  light: '#33FF85',
  main: '#00FF66',
  dark: '#00E65C',
  darker: '#007A3D',
  contrastText: '#0B1320',
};

const SECONDARY = {
  lighter: '#D6E4FF',
  light: '#84A9FF',
  main: '#3366FF',
  dark: '#1939B7',
  darker: '#091A7A',
  contrastText: '#FFFFFF',
};

const INFO = {
  lighter: '#DBEAFE',
  light: '#60A5FA',
  main: '#3B82F6',
  dark: '#1D4ED8',
  darker: '#1E3A8A',
  contrastText: '#FFFFFF',
};

const SUCCESS = {
  lighter: '#CCFFE8',
  light: '#33FF85',
  main: '#00D95A',
  dark: '#00B84C',
  darker: '#007A3D',
  contrastText: '#0B1320',
};

const WARNING = {
  lighter: '#FEF3C7',
  light: '#FCD34D',
  main: '#F59E0B',
  dark: '#B45309',
  darker: '#78350F',
  contrastText: '#0B1320',
};

const ERROR = {
  lighter: '#FEE2E2',
  light: '#F87171',
  main: '#EF4444',
  dark: '#B91C1C',
  darker: '#7F1D1D',
  contrastText: '#FFFFFF',
};

const COMMON = {
  common: { black: '#000000', white: '#FFFFFF' },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  divider: alpha(GREY[500], 0.24),
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

export default function palette(themeMode: 'light' | 'dark') {
  const light = {
    ...COMMON,
    mode: 'light',
    text: {
      primary: '#0B1320',
      secondary: '#4b5563',
      disabled: GREY[500],
    },
    background: { paper: '#FFFFFF', default: '#f6fff8', neutral: '#d7e3dc' },
    action: {
      ...COMMON.action,
      active: GREY[600],
    },
  } as const;

  const dark = {
    ...COMMON,
    mode: 'dark',
    text: {
      primary: '#E5E7EB',
      secondary: '#A3FFB3',
      disabled: GREY[600],
    },
    background: {
      paper: '#1F2937',
      default: '#0B1320',
      neutral: '#263445',
    },
    action: {
      ...COMMON.action,
      active: GREY[500],
    },
  } as const;

  return themeMode === 'light' ? light : dark;
}
